// @ts-nocheck
import { Stack, Title, Text, SimpleGrid, Code } from '@mantine/core';
import { Card } from '../../../../components/ui';
import { LineChartWidget } from '../../../../components/advanced/Widgets';
import {
  mockLineChartData,
  mockMultiSeriesLineData,
} from '../../mockData/widgets';

export default function LineChartWidgetsPage() {
  return (
    <Stack gap="xl">
      <Stack gap="xs">
        <Title order={2}>Line Chart Widgets</Title>
        <Text c="dimmed" size="sm">
          Time-series line chart widgets for tracking trends over time. Supports multiple data
          series, date range filtering, and export to PNG/SVG/CSV formats.
        </Text>
      </Stack>

      {/* Single Series Line Chart */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Single Series Line Chart</Title>
          <Text size="sm" c="dimmed">
            Basic line chart showing a single metric over time.
          </Text>

          <LineChartWidget
            title="Daily Active Users"
            subtitle="Last 365 days"
            data={mockLineChartData}
            lines={[
              { dataKey: 'value', name: 'Active Users', color: '#228be6', strokeWidth: 2 },
            ]}
            height={350}
            showExport
          />

          <Code block>
{`<LineChartWidget
  title="Daily Active Users"
  data={dailyData}
  lines={[
    { dataKey: 'value', name: 'Active Users', color: '#228be6' }
  ]}
  showExport
/>`}
          </Code>
        </Stack>
      </Card>

      {/* Multi-Series Line Chart */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Multi-Series Line Chart</Title>
          <Text size="sm" c="dimmed">
            Compare multiple metrics on the same chart with different colored lines.
          </Text>

          <LineChartWidget
            title="User Engagement Metrics"
            subtitle="Track user activity over time"
            data={mockMultiSeriesLineData}
            lines={[
              { dataKey: 'users', name: 'Users', color: '#228be6', strokeWidth: 2 },
              { dataKey: 'sessions', name: 'Sessions', color: '#40c057', strokeWidth: 2 },
              { dataKey: 'pageviews', name: 'Pageviews', color: '#7950f2', strokeWidth: 2 },
            ]}
            height={350}
            showExport
            showLegend
          />

          <Code block>
{`<LineChartWidget
  title="User Engagement Metrics"
  data={engagementData}
  lines={[
    { dataKey: 'users', name: 'Users', color: '#228be6' },
    { dataKey: 'sessions', name: 'Sessions', color: '#40c057' },
    { dataKey: 'pageviews', name: 'Pageviews', color: '#7950f2' }
  ]}
  showExport
  showLegend
/>`}
          </Code>
        </Stack>
      </Card>

      {/* With Custom Styling */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Customized Line Chart</Title>
          <Text size="sm" c="dimmed">
            Customize stroke width, hide grid, and adjust chart appearance.
          </Text>

          <LineChartWidget
            title="Revenue Trend"
            subtitle="Monthly recurring revenue"
            data={mockLineChartData.slice(0, 180)}
            lines={[
              { dataKey: 'value', name: 'MRR', color: '#12b886', strokeWidth: 3, dot: false },
            ]}
            height={300}
            showGrid={false}
            showExport
          />

          <Code block>
{`<LineChartWidget
  title="Revenue Trend"
  data={revenueData}
  lines={[
    { dataKey: 'value', name: 'MRR', color: '#12b886', strokeWidth: 3, dot: false }
  ]}
  showGrid={false}
/>`}
          </Code>
        </Stack>
      </Card>

      {/* Date Range Filtering */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>With Date Range Filtering</Title>
          <Text size="sm" c="dimmed">
            Built-in date range filters (7d, 30d, 90d, 1y, all) for quick time period selection.
          </Text>

          <LineChartWidget
            title="Server Response Time"
            subtitle="Average response time in milliseconds"
            data={mockLineChartData}
            lines={[
              { dataKey: 'value', name: 'Response Time (ms)', color: '#fa5252', strokeWidth: 2 },
            ]}
            dateRange="30d"
            height={350}
            showExport
          />

          <Code block>
{`<LineChartWidget
  title="Server Response Time"
  data={performanceData}
  lines={[
    { dataKey: 'value', name: 'Response Time (ms)', color: '#fa5252' }
  ]}
  dateRange="30d"
  onDateRangeChange={(range) => console.log(range)}
/>`}
          </Code>
        </Stack>
      </Card>

      {/* Use Cases */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Common Use Cases</Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            <Stack gap="xs">
              <Text size="sm" fw={500}>Performance Monitoring</Text>
              <Text size="xs" c="dimmed">
                Track server response times, API latency, and system performance metrics over time.
              </Text>
            </Stack>
            <Stack gap="xs">
              <Text size="sm" fw={500}>User Analytics</Text>
              <Text size="xs" c="dimmed">
                Visualize user growth, engagement metrics, and activity patterns across time periods.
              </Text>
            </Stack>
            <Stack gap="xs">
              <Text size="sm" fw={500}>Business Metrics</Text>
              <Text size="xs" c="dimmed">
                Monitor revenue trends, conversion rates, and other KPIs to identify growth opportunities.
              </Text>
            </Stack>
            <Stack gap="xs">
              <Text size="sm" fw={500}>Trend Comparison</Text>
              <Text size="xs" c="dimmed">
                Compare multiple metrics on the same timeline to understand correlations and patterns.
              </Text>
            </Stack>
          </SimpleGrid>
        </Stack>
      </Card>
    </Stack>
  );
}
